import styled from "styled-components";
import colours from "../../lib/colours";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import { guidelines } from "../../lib/constants";

const Container = styled.section`
  padding-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin-top: 24px;
  margin-bottom: -8px;
  font-weight: 600;
  color: ${colours.th1};
`;

interface IProps {
  pathway: string;
}

function ReviewDownloadRemoval({ pathway }: IProps) {
  const detailed = pathway === "Detailed";

  const renderNonDetailedContent = () => {
    return (
      <>
        <p>
          Your proposal to remove native vegetation falls under the{" "}
          <span style={{ fontWeight: 500 }}>{pathway}</span> Assessment Pathway.
          The application must meet all application requirements outlined in
          Table 4 of the{" "}
          <Link
            href={guidelines}
            target="_blank"
            title="Guidelines for the removal, destruction or lopping of native vegetation"
            rel="noopener noreferrer"
          >
            Guidelines for the removal, destruction or lopping of native
            vegetation
          </Link>
          <ExternalLinkIcon /> (the Guidelines).
        </p>
        <p>
          If you have finished mapping your clearing proposal and addressed the
          application requirements on the previous page, proceed with the
          following steps:
        </p>
        <ol>
          <li>
            Consider opportunities to further avoid and minimise impacts on
            native vegetation. If further native vegetation removal can be
            avoided, remap your proposal.
          </li>
          <li>
            Export the mapped native vegetation dataset for your future use.
            This dataset can be uploaded to the system and edited at a later
            date.
          </li>
          <li>Download the Native Vegetation Removal Report.</li>
          <li>
            Ensure you have addressed all application requirements outlined in
            Table 4 of the Guidelines. Assuming you have addressed the
            application requirements on the previous page, the Native Vegetation
            Removal Report will cover most of the application requirements
            outlined in Table 4 of the Guidelines. The following outstanding
            requirements will need to be addressed in your approval application:
            <ul>
              <li>
                Application Requirement 3 - Recent, dated photographs of the
                native vegetation to be removed.
              </li>
              <li>
                Application Requirement 6 – If an approved Property Vegetation
                Plan (PVP) applies to the property, it must be provided with the
                application.
              </li>
            </ul>
          </li>
          <li>
            Prepare your application and apply for approval to remove, destroy
            or lop native vegetation. In most cases, approval will be sought
            from the local Council under Clauses 52.17 or 52.16 of the local
            planning scheme; however, alternative approval provisions may apply.
            Please contact your local Council if you require assistance with
            determining the applicable approvals process.
          </li>
        </ol>
      </>
    );
  };
  const renderDetailedContent = () => {
    return (
      <>
        <p>
          Your proposal to remove native vegetation falls under the{" "}
          <span style={{ fontWeight: 500 }}>Detailed</span> Assessment Pathway.
          The application must meet all application requirements outlined in
          Tables 4 and 5 of the{" "}
          <Link
            href={guidelines}
            target="_blank"
            title="Guidelines for the removal, destruction or lopping of native vegetation"
            rel="noopener noreferrer"
          >
            Guidelines for the removal, destruction or lopping of native
            vegetation
          </Link>
          <ExternalLinkIcon /> (the Guidelines).
        </p>
        <p>
          If you have finished identifying your clearing proposal, proceed with
          the following steps:
        </p>
        <ol>
          <li>
            Consider opportunities to further avoid and minimise impacts on
            native vegetation. If further native vegetation removal can be
            avoided, revise your proposal.
          </li>
          <li>
            Ensure that the native vegetation dataset is saved for your future
            use. This dataset can be uploaded to the system and edited at a
            later date.
          </li>
          <li>Download the Native Vegetation Removal Report.</li>
          <li>
            Ensure you have addressed all application requirements outlined in
            Tables 4 and 5 of the{" "}
            <Link
              href={guidelines}
              target="_blank"
              title="Guidelines for the removal, destruction or lopping of native vegetation"
              rel="noopener noreferrer"
            >
              Guidelines
            </Link>
            <ExternalLinkIcon />.
          </li>
          <li>
            Prepare your application and apply for approval to remove, destroy
            or lop native vegetation. In most cases, approval will be sought
            from the local Council under Clauses 52.17 or 52.16 of the local
            planning scheme; however, alternative approval provisions may apply.
            Please contact your local Council if you require assistance with
            determining the applicable approvals process.
          </li>
        </ol>
      </>
    );
  };

  return (
    <Container>
      <SectionTitle>Next steps</SectionTitle>
      {detailed ? renderDetailedContent() : renderNonDetailedContent()}
    </Container>
  );
}

export default ReviewDownloadRemoval;
